<template>
  <button
    :class="[`${colour}`, `${size}`, { 'disabled ': disabled }]"
    :disabled="disabled || loading"
    :style="customStyleOverrides"
    class="flex flex-row border justify-center rounded-[60px] relative ot-button cursor-pointer"
    tabindex="0"
    @click="$emit('click')">
    <transition name="fade">
      <PulseLoader
        v-if="loading"
        :color="colour === 'white' ? `var(--color-primary)` : '#fff'"
        class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        size="5px" />
    </transition>
    <div :class="[{ 'h-0 invisible': loading }, typography]">
      <slot :class="typography"> Default button text </slot>
    </div>
  </button>
</template>

<script lang="ts" setup>
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'

interface Props {
  disabled?: boolean
  loading?: boolean
  customStyleOverrides?: string
  size?: 'large' | 'medium' | 'small' | 'mobile'
  colour?: 'blue' | 'red' | 'white' | 'sky' | 'dark-red' | 'black' | 'transparent'
  typography?: 'button-1' | 'button-2' | 'button-3'
}

withDefaults(defineProps<Props>(), {
  disabled: false,
  loading: false,
  customStyleOverrides: '',
  size: 'medium',
  colour: 'blue',
  typography: 'button-1',
})

defineEmits<{
  (e: 'click'): void
}>()
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.75s;
}

.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.ot-button:focus-visible {
  outline: var(--color-blue-1);
  outline-style: solid;
  outline-offset: 0px;
}

/* SIZE */
.large {
  min-height: 50px;
  padding: 16px 50px;
  font-weight: 700;
  font-size: 14px;
  line-height: 120%;
}

.medium {
  min-height: 40px;
  padding: 13px 40px;
  font-weight: 700;
  font-size: 12px;
  line-height: 120%;
}

.small {
  min-height: 34px;
  padding: 10px 20px;
  font-weight: 500;
  font-size: 12px;
  line-height: 120%;
}

.mobile {
  min-height: 60px;
  padding: 20px 20px;
  font-weight: 700;
  font-size: 18px;
  line-height: 120%;
}

/* TYPE */
.blue {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
  color: white;
}

.blue:hover {
  background-color: var(--color-blue-1);
  border-color: var(--color-blue-1);
}

.red {
  background-color: var(--color-secondary);
  border-color: var(--color-secondary);
  color: white;
}

.red:hover {
  background-color: var(--color-red-1);
  border-color: var(--color-red-1);
}

.dark-red {
  background-color: var(--color-red-1);
  border-color: var(--color-red-1);
  color: white;
}

.dark-red:hover {
  background-color: var(--color-red-light);
  border-color: var(--color-red-light);
  color: var(--color-red-1);
}

.white {
  background-color: white;
  border-color: var(--color-grey-1);
  color: var(--color-primary);
}

.white:hover {
  background-color: var(--color-grey-4);
}

.sky {
  background-color: white;
  border-color: var(--color-blue-3);
  color: var(--color-blue-3);
}

.sky:hover {
  background-color: var(--color-grey-4);
}

.black {
  background-color: black;
  border-color: black;
  color: white;
}

.black:hover {
  background-color: var(--color-grey-2);
  border-color: var(--color-grey-2);
}

.transparent {
  background-color: transparent;
  border-color: white;
  color: white;
  border-width: 1px;
  box-shadow: 0 0 1px 1px white inset;
}

.disabled {
  background-color: var(--color-grey-4);
  border: 1px solid var(--color-grey-3);
  color: var(--color-grey-3);
  pointer-events: none;
  cursor: not-allowed;
}

button:disabled {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}
</style>
